// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Spinner } from '@loa/ui-components';
import { clientListApi } from '../api/clientListApi';
import { profileFormApi } from '../api/profileFormApi';
import type { FactFindSchema, ProfileFormVersion } from '../api/profileFormApi';
import type { ClientDetailDTO } from '../types/clientList';
import { RiskAssessmentReadout } from '../components/risk/RiskAssessmentReadout';
import { VersionHistoryControl } from '../components/profileform/VersionHistoryControl';
import { usePageHeader } from '../contexts/PageHeaderContext';

/**
 * Advisor read-only Risk Assessment page for ONE client (Advisor 2.0 Figma
 * node 1540-19871). Risk is a PER_CLIENT assessment — this page shows the
 * selected client's own record: header with the client's name, "Last
 * Updated", and a Version History picker; the body renders any chosen
 * version through the shared `RiskAssessmentReadout` (result + answered
 * questions). View-only — clients own the editing.
 */
export function ClientRiskAssessmentPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const { setBreadcrumbItems, setBackTo } = usePageHeader();

  const [client, setClient] = useState<ClientDetailDTO | null>(null);
  const [schema, setSchema] = useState<FactFindSchema | null>(null);
  const [versions, setVersions] = useState<ProfileFormVersion[]>([]);
  const [selected, setSelected] = useState<ProfileFormVersion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!clientId) return;
      setLoading(true);
      setError(null);
      try {
        const detail = await clientListApi.getClientDetail(clientId);
        const [s, all] = await Promise.all([
          profileFormApi.getSchema(detail.profileId, 'RISK_ATTITUDE'),
          profileFormApi.listVersions(detail.profileId, 'RISK_ATTITUDE', clientId),
        ]);
        if (cancelled) return;
        setClient(detail);
        setSchema(s);
        setVersions(all);
        setSelected(all[0] ?? null); // newest first
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load risk assessment:', err);
        setError('The risk assessment could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    if (client) {
      setBreadcrumbItems([
        { label: 'Relationships', to: '/relationships' },
        { label: `${client.firstName} ${client.lastName}`, to: `/clients/${client.id}` },
        { label: 'Risk Assessment' },
      ]);
      setBackTo(`/clients/${client.id}`);
    }
    return () => {
      setBreadcrumbItems(null);
      setBackTo(null);
    };
  }, [client, setBreadcrumbItems, setBackTo]);

  const lastUpdated = versions[0]?.submittedAt
    ? new Date(versions[0].submittedAt).toLocaleDateString()
    : null;

  return (
    <div className="bg-page-bg p-lg min-h-screen overflow-auto">
      <div
        data-testid="client-risk-assessment"
        className="flex flex-col flex-1 min-h-0 w-full max-w-page self-center mx-auto"
      >
        <div className="self-stretch rounded-lg bg-white shadow-card px-md py-lg flex flex-col gap-lg">
          <div className="flex flex-col items-start gap-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-xs">
              <h1 className="text-black font-sans text-body-01 font-medium leading-normal m-0">
                Risk Assessment
              </h1>
              {client && (
                <p className="m-0 font-sans text-body-03 text-grey-01" data-testid="risk-client-name">
                  {client.firstName} {client.lastName}
                </p>
              )}
            </div>
            <div className="flex items-center gap-md">
              {lastUpdated && (
                <span className="font-sans text-body-03 text-grey-01" data-testid="risk-last-updated">
                  Last Updated: {lastUpdated}
                </span>
              )}
              <VersionHistoryControl
                versions={versions}
                selectedVersionNo={selected?.versionNo ?? 0}
                onSelect={setSelected}
              />
            </div>
          </div>

          {loading ? (
            <div data-testid="risk-assessment-loading" className="flex justify-center py-lg">
              <Spinner />
            </div>
          ) : error ? (
            <Alert variant="error" header="Error Loading Risk Assessment">
              <p>{error}</p>
            </Alert>
          ) : schema && selected ? (
            <>
              {selected.versionNo !== versions[0]?.versionNo && (
                <Alert variant="info" header={`Viewing version ${selected.versionNo}`}>
                  <p>
                    This is a historical version submitted on{' '}
                    {selected.submittedAt
                      ? new Date(selected.submittedAt).toLocaleDateString()
                      : 'an earlier date'}
                    . The latest is version {versions[0]?.versionNo}.
                  </p>
                </Alert>
              )}
              <RiskAssessmentReadout schema={schema} latest={selected} />
            </>
          ) : (
            <Alert variant="info" header="No Risk Assessment">
              <p>No risk assessment result available for this client.</p>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClientRiskAssessmentPage;
